import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { IdCard, RefreshCw, Copy, Phone, Mail, MessageCircle, Send, Pencil, Check } from "lucide-react";

const gg = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "—");
const eur = (c) => `${(Number(c || 0) / 100).toFixed(2).replace(".", ",")} €`;
const val = (s) => (s && String(s).trim() ? s : "—");

const Riga = ({ l, v, testid }) => (
  <div className="min-w-0">
    <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{l}</p>
    <p className="truncate text-[12px] font-semibold text-slate-200" data-testid={testid}>{val(v)}</p>
  </div>
);

/** Tutto su un cliente: dati della registrazione, abbonamento, rinnovi, pagamenti e avvio. */
export const SchedaCliente = ({ client, tenant }) => {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await client.get(`/admin/tenants/${tenant.id}/anagrafica`);
      setD(r.data);
      setF({
        contact: r.data.anagrafica.contact || "",
        phone: r.data.anagrafica.phone || "",
        email: r.data.anagrafica.email || "",
        address: r.data.anagrafica.address || "",
        notes: r.data.anagrafica.notes || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scheda non disponibile");
    }
  }, [client, tenant.id]);

  useEffect(() => {
    load();
    setEdit(false);
  }, [load]);

  if (!d) return null;
  const a = d.anagrafica;
  const ab = d.abbonamento;
  const tel = (a.phone || "").replace(/[^\d+]/g, "");

  const salva = async () => {
    try {
      await client.patch(`/admin/tenants/${tenant.id}`, f);
      toast.success("Dati del cliente aggiornati");
      setEdit(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Modifica non salvata");
    }
  };

  const sollecita = async () => {
    try {
      const r = await client.post(`/admin/tenants/${tenant.id}/sollecito`);
      toast.success(`Sollecito inviato a ${r.data.to}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sollecito non inviato");
    }
  };

  const copiaTutto = () => {
    navigator.clipboard?.writeText(
      [`${a.name} (${a.slug})`, a.activity && `Attività: ${a.activity}`, a.vat && `P.IVA ${a.vat}`,
        a.contact && `Referente: ${a.contact}`, a.phone && `Tel ${a.phone}`, a.email && `Email ${a.email}`,
        a.address && `Indirizzo: ${a.address}`, `Registrato il ${gg(a.created_at)}`,
        `Piano ${ab.plan_name}${ab.current_period_end ? ` fino al ${gg(ab.current_period_end)}` : ""}`,
      ].filter(Boolean).join("\n"));
    toast.success("Dati del cliente copiati");
  };

  return (
    <div className="space-y-4 rounded-md bg-slate-900/60 p-3" data-testid="scheda-cliente">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
          <IdCard className="h-3.5 w-3.5" /> Scheda del cliente
        </p>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" className="text-slate-400" onClick={copiaTutto} data-testid="scheda-copia">
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="ghost" className="text-slate-400" onClick={load} data-testid="scheda-refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Anagrafica della registrazione */}
      {edit ? (
        <div className="grid gap-2 sm:grid-cols-2" data-testid="scheda-form">
          {[["contact", "Referente (nome e cognome)"], ["phone", "Telefono"], ["email", "Email"],
            ["address", "Indirizzo del locale"], ["notes", "Note"]].map(([k, l]) => (
            <label key={k} className="text-[11px] text-slate-400">
              {l}
              <Input className="mt-1 h-9" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                     data-testid={`scheda-input-${k}`} />
            </label>
          ))}
          <div className="flex items-end gap-2">
            <Button size="sm" onClick={salva} data-testid="scheda-salva">
              <Check className="mr-1 h-3.5 w-3.5" /> Salva
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEdit(false)} data-testid="scheda-annulla">
              Annulla
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Riga l="Attività" v={a.name} testid="scheda-nome" />
            <Riga l="Tipo di locale" v={a.activity} testid="scheda-attivita" />
            <Riga l="P.IVA" v={a.vat} testid="scheda-piva" />
            <Riga l="Codice attività" v={a.slug} testid="scheda-slug" />
            <Riga l="Referente" v={a.contact} testid="scheda-referente" />
            <Riga l="Telefono" v={a.phone} testid="scheda-telefono" />
            <Riga l="Email" v={a.email} testid="scheda-email" />
            <Riga l="Indirizzo" v={a.address} testid="scheda-indirizzo" />
            <Riga l="Registrato il" v={gg(a.created_at)} testid="scheda-registrato" />
            <Riga l="Ultimo accesso" v={gg(a.last_access)} testid="scheda-ultimo-accesso" />
            <Riga l="Arrivato da" v={a.origine} testid="scheda-origine" />
            <Riga l="Squadra dichiarata" v={a.team_size} testid="scheda-squadra" />
          </div>
          {a.notes && <p className="text-[11px] text-slate-400">Note: {a.notes}</p>}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="scheda-modifica">
              <Pencil className="mr-1 h-3.5 w-3.5" /> Completa i dati
            </Button>
            {tel && (
              <>
                <a className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-700 px-2.5 text-xs font-semibold text-slate-300"
                   href={`tel:${tel}`} data-testid="scheda-chiama">
                  <Phone className="h-3.5 w-3.5" /> Chiama
                </a>
                <a className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-700 px-2.5 text-xs font-semibold text-slate-300"
                   href={`https://wa.me/${tel.replace("+", "")}`} target="_blank" rel="noreferrer" data-testid="scheda-whatsapp">
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
              </>
            )}
            {a.email && (
              <a className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-700 px-2.5 text-xs font-semibold text-slate-300"
                 href={`mailto:${a.email}`} data-testid="scheda-mailto">
                <Mail className="h-3.5 w-3.5" /> Scrivi
              </a>
            )}
          </div>
        </>
      )}

      {/* Abbonamento */}
      <div className="grid grid-cols-2 gap-3 border-t border-slate-800 pt-3 sm:grid-cols-4">
        <Riga l="Piano" v={ab.plan_name} testid="scheda-piano" />
        <Riga l="Attivo dal" v={gg(ab.activated_at)} testid="scheda-attivo-dal" />
        <Riga l="Scade il" v={gg(ab.current_period_end)} testid="scheda-scade" />
        <Riga l="Come è attivo" v={ab.stripe ? "abbonamento Stripe" : ab.source} testid="scheda-fonte" />
      </div>

      {/* Rinnovi */}
      <div className="border-t border-slate-800 pt-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Attivazione e rinnovi</p>
        {d.rinnovi.length === 0 ? (
          <p className="mt-1 text-[11px] text-slate-500">
            Nessun rinnovo registrato: lo storico parte dal primo rinnovo o pagamento da qui in avanti.
          </p>
        ) : (
          <ul className="mt-1.5 space-y-1.5" data-testid="rinnovi-lista">
            {d.rinnovi.map((r) => (
              <li key={r.id} className="flex flex-wrap items-baseline gap-x-2 border-b border-slate-800 pb-1.5 text-[11px] last:border-0">
                <span className="font-num font-semibold text-slate-200">{gg(r.at)}</span>
                <span className="text-slate-300">{r.plan_name}</span>
                {r.months ? <span className="font-num text-slate-500">{r.months} mesi</span> : null}
                <span className="text-slate-500">· {r.origine}</span>
                {r.until && <span className="font-num text-slate-500">→ {gg(r.until)}</span>}
                {r.note && <span className="text-slate-600">{r.note}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagamenti */}
      <div className="border-t border-slate-800 pt-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Pagamenti</p>
        {d.pagamenti.length === 0 ? (
          <p className="mt-1 text-[11px] text-slate-500" data-testid="pagamenti-vuoto">Nessun pagamento registrato per questo cliente.</p>
        ) : (
          <ul className="mt-1.5 space-y-1.5" data-testid="pagamenti-lista">
            {d.pagamenti.map((p) => (
              <li key={p.session_id} className="flex flex-wrap items-baseline gap-x-2 border-b border-slate-800 pb-1.5 text-[11px] last:border-0">
                <span className="font-num font-semibold text-slate-200">{gg(p.created_at)}</span>
                <span className="font-num text-slate-300">{eur(p.amount)}</span>
                <span className="text-slate-500">{p.plan}</span>
                <span className={p.payment_status === "paid" ? "text-emerald-400" : "text-amber-400"}>
                  {p.payment_status === "paid" ? "pagato" : p.payment_status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Avvio e sollecito */}
      <div className="space-y-2 border-t border-slate-800 pt-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
          Avvio al {d.avvio.percent}%
        </p>
        {d.avvio.missing.length === 0 ? (
          <p className="text-[11px] text-emerald-400" data-testid="avvio-completo">Avvio completo: è pronto a lavorare.</p>
        ) : (
          <>
            <p className="text-[11px] text-amber-300" data-testid="avvio-mancanti">
              Gli manca: {d.avvio.missing.join(" · ")}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={sollecita} data-testid="scheda-sollecito">
                <Send className="mr-1 h-3.5 w-3.5" /> Manda sollecito
              </Button>
              <span className="text-[11px] text-slate-500">
                {d.sollecito_at ? `Ultimo sollecito il ${gg(d.sollecito_at)}` : "Nessun sollecito mandato"}
                {" · "}parte anche da solo dopo 3 giorni, max 1 ogni 7
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
